import type { HoldingRow, HoldingsResponse } from '~/api/client'
import { shareBasisPoints } from './format'
import { SERIES_SLOTS } from './theme'

export type AllocationCutId = 'assetClass' | 'currency' | 'entity' | 'liquidity'

export interface AllocationCut {
  id: AllocationCutId
  label: string
  description: string
  /** Undefined where the row cannot be placed in this cut at all. */
  categoryOf(row: HoldingRow): { key: string; label: string } | undefined
  /** Categories are ranked by this list first so a colour follows a category, not its size. */
  order?: readonly string[]
}

const ASSET_CLASS_LABELS: Record<string, string> = {
  cash: 'Cash',
  equity: 'Equity',
  etf: 'ETF',
  fund: 'Fund',
  crypto: 'Crypto',
  private: 'Private',
}

export const ALLOCATION_CUTS: readonly AllocationCut[] = [
  {
    id: 'assetClass',
    label: 'Asset class',
    description: 'What the money is in.',
    order: ['cash', 'equity', 'etf', 'fund', 'crypto', 'private'],
    categoryOf: (row) => ({
      key: row.instrumentKind,
      label: ASSET_CLASS_LABELS[row.instrumentKind] ?? row.instrumentKind,
    }),
  },
  {
    id: 'currency',
    label: 'Currency',
    description: 'The currency each holding is priced in, before it is converted.',
    categoryOf: (row) =>
      row.priceCurrency === null ? undefined : { key: row.priceCurrency, label: row.priceCurrency },
  },
  {
    id: 'entity',
    label: 'Entity',
    // Offered only where there is more than one entity, and two entities are as
    // likely to be two people as a person and a company — so the description
    // cannot name a company the reader may not have. It says which kinds of
    // thing a holding can be filed under, and claims neither for anybody.
    description: 'Who holds it — which person or company each holding is filed under.',
    categoryOf: (row) => ({ key: row.entityKey, label: row.entityName }),
  },
  {
    id: 'liquidity',
    label: 'Liquidity',
    description: 'What could be turned into money, and what could not.',
    order: ['liquid', 'illiquid'],
    categoryOf: (row) => ({
      key: row.liquid ? 'liquid' : 'illiquid',
      label: row.liquid ? 'Liquid' : 'Illiquid',
    }),
  },
]

export const DEFAULT_CUT: AllocationCutId = 'assetClass'

/**
 * The cuts that can tell this household something over the rows it has.
 *
 * A cut whose rows all land in one category draws a single band at 100 % and a
 * drift table stating the same figure twice, under a heading named after a
 * distinction the household does not have. Asked of every row across the range
 * rather than of today alone, so a household that once held through a company
 * keeps the cut for the stretch where the two genuinely differed.
 *
 * Empty when no cut can draw more than one band — everything is in one place.
 * That is reported rather than papered over with the full list, because the page
 * says so in words instead of offering four charts of a single band.
 */
export function cutsWithMoreThanOneBand(
  responses: readonly HoldingsResponse[],
): readonly AllocationCut[] {
  const telling = ALLOCATION_CUTS.filter((cut) => {
    const keys = new Set<string>()
    for (const response of responses) {
      for (const row of response.rows) {
        const category = cut.categoryOf(row)
        if (category !== undefined) keys.add(category.key)
      }
    }
    return keys.size > 1
  })
  return telling
}

export function cutById(id: string): AllocationCut {
  return ALLOCATION_CUTS.find((cut) => cut.id === id) ?? ALLOCATION_CUTS[0]
}

export const OTHER_KEY = '__other__'
const MAX_NAMED_CATEGORIES = SERIES_SLOTS

export interface AllocationBucket {
  key: string
  label: string
  valueMinor: number
  /** Part of `valueMinor` that rests on unconfirmed or carried-forward holdings. */
  flaggedMinor: number
}

export interface AllocationSnapshot {
  date: string
  totalMinor: number
  buckets: AllocationBucket[]
  /** Holdings with no price: left out rather than counted as zero. */
  unpricedSymbols: string[]
  /** Holdings this cut cannot place, e.g. a currency cut and an unpriced row. */
  unclassifiedSymbols: string[]
}

export function bucketHoldings(response: HoldingsResponse, cut: AllocationCut): AllocationSnapshot {
  const buckets = new Map<string, AllocationBucket>()
  const unpricedSymbols: string[] = []
  const unclassifiedSymbols: string[] = []
  let totalMinor = 0

  for (const row of response.rows) {
    if (row.valueMinor === null) {
      unpricedSymbols.push(row.symbol)
      continue
    }

    const category = cut.categoryOf(row)
    if (!category) {
      unclassifiedSymbols.push(row.symbol)
      continue
    }

    const bucket = buckets.get(category.key) ?? {
      key: category.key,
      label: category.label,
      valueMinor: 0,
      flaggedMinor: 0,
    }
    bucket.valueMinor += row.valueMinor
    if (!row.confirmed || row.stale) bucket.flaggedMinor += row.valueMinor
    buckets.set(category.key, bucket)
    totalMinor += row.valueMinor
  }

  return {
    date: response.date ?? '',
    totalMinor,
    buckets: [...buckets.values()],
    unpricedSymbols: [...new Set(unpricedSymbols)].sort(),
    unclassifiedSymbols: [...new Set(unclassifiedSymbols)].sort(),
  }
}

export interface AllocationCategory {
  key: string
  label: string
  /** Fixed for the life of a cut, so narrowing the range never repaints a category. */
  slot: number
}

export interface AllocationRow {
  date: string
  totalMinor: number
  valueMinor: Record<string, number>
  shareBp: Record<string, number>
  flaggedMinor: number
}

export interface AllocationTimeline {
  categories: AllocationCategory[]
  rows: AllocationRow[]
  unpricedSymbols: string[]
  unclassifiedSymbols: string[]
}

/**
 * Colours run out before categories do, so anything past the palette is folded
 * into one named rest rather than given a generated hue.
 */
export function rankCategories(
  snapshots: readonly AllocationSnapshot[],
  cut: AllocationCut,
): AllocationCategory[] {
  const totals = new Map<string, { label: string; valueMinor: number }>()
  for (const snapshot of snapshots) {
    for (const bucket of snapshot.buckets) {
      const seen = totals.get(bucket.key) ?? { label: bucket.label, valueMinor: 0 }
      seen.valueMinor += bucket.valueMinor
      totals.set(bucket.key, seen)
    }
  }

  const order = cut.order ?? []
  const ranked = [...totals.entries()].sort(([aKey, a], [bKey, b]) => {
    const aRank = order.indexOf(aKey)
    const bRank = order.indexOf(bKey)
    if (aRank !== bRank) return (aRank < 0 ? order.length : aRank) - (bRank < 0 ? order.length : bRank)
    return b.valueMinor - a.valueMinor || aKey.localeCompare(bKey)
  })

  const kept = ranked.length > MAX_NAMED_CATEGORIES ? biggest(ranked, MAX_NAMED_CATEGORIES) : ranked
  const categories = kept.map(([key, { label }], index) => ({ key, label, slot: index }))

  if (kept.length < ranked.length) {
    categories.push({ key: OTHER_KEY, label: 'Other', slot: -1 })
  }
  return categories
}

function biggest(
  ranked: readonly (readonly [string, { label: string; valueMinor: number }])[],
  limit: number,
): (readonly [string, { label: string; valueMinor: number }])[] {
  const survivors = new Set(
    [...ranked]
      .sort(([, a], [, b]) => b.valueMinor - a.valueMinor)
      .slice(0, limit)
      .map(([key]) => key),
  )
  return ranked.filter(([key]) => survivors.has(key))
}

export function buildTimeline(
  snapshots: readonly AllocationSnapshot[],
  cut: AllocationCut,
): AllocationTimeline {
  const categories = rankCategories(snapshots, cut)
  const named = new Set(categories.map((c) => c.key))

  const rows = snapshots.map((snapshot) => {
    const valueMinor: Record<string, number> = {}
    for (const category of categories) valueMinor[category.key] = 0

    let flaggedMinor = 0
    for (const bucket of snapshot.buckets) {
      const key = named.has(bucket.key) ? bucket.key : OTHER_KEY
      valueMinor[key] = (valueMinor[key] ?? 0) + bucket.valueMinor
      flaggedMinor += bucket.flaggedMinor
    }

    const shareBp: Record<string, number> = {}
    for (const key of Object.keys(valueMinor)) {
      shareBp[key] = shareBasisPoints(valueMinor[key] ?? 0, snapshot.totalMinor)
    }

    return { date: snapshot.date, totalMinor: snapshot.totalMinor, valueMinor, shareBp, flaggedMinor }
  })

  return {
    categories,
    rows,
    unpricedSymbols: [...new Set(snapshots.flatMap((s) => s.unpricedSymbols))].sort(),
    unclassifiedSymbols: [...new Set(snapshots.flatMap((s) => s.unclassifiedSymbols))].sort(),
  }
}

export interface CategoryDrift {
  key: string
  label: string
  slot: number
  startShareBp: number
  endShareBp: number
  driftBp: number
  endValueMinor: number
}

/** Drift is the point of the view, so it is computed rather than left to the eye. */
export function driftOver(timeline: AllocationTimeline): CategoryDrift[] {
  const first = timeline.rows[0]
  const last = timeline.rows[timeline.rows.length - 1]
  if (!first || !last) return []

  return timeline.categories
    .map((category) => {
      const startShareBp = first.shareBp[category.key] ?? 0
      const endShareBp = last.shareBp[category.key] ?? 0
      return {
        key: category.key,
        label: category.label,
        slot: category.slot,
        startShareBp,
        endShareBp,
        driftBp: endShareBp - startShareBp,
        endValueMinor: last.valueMinor[category.key] ?? 0,
      }
    })
    .sort((a, b) => b.endShareBp - a.endShareBp)
}
